/**
 * Apple-Watch-style mockup with crown / side button + cushion display.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";
import { watchFrame, screenContent } from "./_mockHelpers.js";

export const watchMockup: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const headline = inputs.text ?? "";
  const caption = inputs.subtext ?? "";
  const isVertical = ctx.format === "9:16";
  const watchW = isVertical ? 380 : 320;

  const lg = typeSize("lg", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const headId = `${clipPrefix}-head`;
  const capId = `${clipPrefix}-cap`;
  const deviceId = `${clipPrefix}-device`;
  const glowId = `${clipPrefix}-glow`;
  const ringId = `${clipPrefix}-ring`;

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "spotlight", accent });

  const inner = screenContent({ media: inputs.media, accent, fallback: "card" });

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:28px;padding:80px;">
    ${caption ? `<div id="${capId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.78}px;letter-spacing:6px;text-transform:uppercase;color:${accent};">${escapeHtml(caption)}</div>` : ""}
    ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.78}px;letter-spacing:-2px;color:${themeColors.fg};line-height:1.02;text-align:center;text-wrap:balance;max-width:${isVertical ? 840 : 1080}px;">${escapeHtml(headline)}</div>` : ""}
    <div style="position:relative;display:flex;align-items:center;justify-content:center;margin-top:16px;">
      <div id="${ringId}" style="position:absolute;width:${watchW * 1.7}px;height:${watchW * 1.7}px;border-radius:9999px;border:1.5px dashed ${withAlpha(accent, 0.45)};"></div>
      <div id="${glowId}" style="position:absolute;width:${watchW * 1.3}px;height:${watchW * 1.3}px;border-radius:9999px;background:radial-gradient(closest-side, ${withAlpha(accent, 0.55)} 0%, transparent 70%);filter:blur(22px);"></div>
      ${watchFrame({ id: deviceId, width: watchW, accent, inner })}
    </div>
  </div>`;

  const init = [
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 14 }) : "",
    caption ? gsapSetLine(`#${capId}`, { opacity: 0, y: 10 }) : "",
    gsapSetLine(`#${deviceId}`, { opacity: 0, scale: 0.6, rotation: -8, transformOrigin: "50% 50%" }),
    gsapSetLine(`#${glowId}`, { opacity: 0, scale: 0.7 }),
    gsapSetLine(`#${ringId}`, { opacity: 0, scale: 0.7 }),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${deviceId}`, { opacity: 1, scale: 1, rotation: 0, duration: 0.9, ease: "raw:\"back.out(1.6)\"" }, 0.1),
    t.to(`#${glowId}`, { opacity: 0.85, scale: 1, duration: 0.85, ease: "raw:\"power2.out\"" }, 0.05),
    t.to(`#${ringId}`, { opacity: 1, scale: 1, duration: 1.0, ease: "raw:\"power2.out\"" }, 0.2),
    t.to(`#${ringId}`, { rotation: 360, duration: clipDurationSec, ease: "raw:\"none\"" }, 0.0),
  ];
  if (caption) tweens.push(t.to(`#${capId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.05));
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.3));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
